/**
 * Cutfruit Design System — generic modal controller (Phase 4).
 * One shared, delegated controller for every `snippets/ds-modal.liquid` instance
 * on the page. Framework-independent (no Alpine dependency) so it works the same
 * inside and outside the product Island.
 *
 * Open:
 *   - click on any `[data-ds-modal-trigger="{id}"]`
 *   - `document.dispatchEvent(new CustomEvent('ds-modal:open', { detail: { id } }))`
 * Close:
 *   - click on `[data-ds-modal-close]` / `[data-ds-modal-overlay]`, or Escape
 *   - `document.dispatchEvent(new CustomEvent('ds-modal:close', { detail: { id } }))`
 *
 * Multiple modals coexist independently; each tracks its own last-focused trigger
 * so focus returns correctly on close. Body scroll is locked while ANY modal is open.
 */
(function () {
  const OPEN_CLASS = 'ds-modal--open';
  let openModals = new Set();
  let lastFocusedTrigger = new Map(); // modalId -> element to restore focus to

  function getModal(id) {
    return document.getElementById(`ds-modal-${id}`);
  }

  // Re-parent every modal to <body> on first paint. `position: fixed` only
  // escapes ancestor stacking/positioning contexts when there's no ancestor
  // with a `transform`, `filter`, `will-change`, or `contain` — several theme
  // components (e.g. splide slide transforms inside the product Island) create
  // exactly that, which trapped the modal behind later page content. Matches
  // the theme's own modal/popup/drawer pattern (teleported to <body> via
  // Alpine's x-teleport) — same fix, framework-independent implementation.
  function moveModalsToBody() {
    document.querySelectorAll('[data-ds-modal]').forEach((modal) => {
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    });
  }

  function getFocusableEls(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  function lockScroll() {
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (openModals.size === 0) {
      document.documentElement.style.overflow = '';
    }
  }

  function openModal(id, triggerEl) {
    const modal = getModal(id);
    if (!modal) return;

    if (triggerEl) {
      lastFocusedTrigger.set(id, triggerEl);
    }

    modal.hidden = false;
    modal.classList.add(OPEN_CLASS);
    openModals.add(id);
    lockScroll();

    const dialog = modal.querySelector('.ds-modal__dialog');
    const closeBtn = modal.querySelector('[data-ds-modal-close]');
    (closeBtn || dialog)?.focus();

    document.dispatchEvent(new CustomEvent('ds-modal:opened', { detail: { id } }));
  }

  function closeModal(id) {
    const modal = getModal(id);
    if (!modal) return;

    modal.classList.remove(OPEN_CLASS);
    modal.hidden = true;
    openModals.delete(id);
    unlockScroll();

    const trigger = lastFocusedTrigger.get(id);
    if (trigger && document.body.contains(trigger)) {
      trigger.focus();
    }
    lastFocusedTrigger.delete(id);

    document.dispatchEvent(new CustomEvent('ds-modal:closed', { detail: { id } }));
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || openModals.size === 0) return;

    // Trap within the topmost (most recently opened) modal.
    const ids = Array.from(openModals);
    const topId = ids[ids.length - 1];
    const modal = getModal(topId);
    if (!modal) return;

    const focusable = getFocusableEls(modal);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ds-modal-trigger]');
    if (trigger) {
      event.preventDefault();
      openModal(trigger.getAttribute('data-ds-modal-trigger'), trigger);
      return;
    }

    const closeBtn = event.target.closest('[data-ds-modal-close]');
    if (closeBtn) {
      const modal = closeBtn.closest('[data-ds-modal]');
      if (modal) closeModal(modal.getAttribute('data-ds-modal-id'));
      return;
    }

    const overlay = event.target.closest('[data-ds-modal-overlay]');
    if (overlay) {
      const modal = overlay.closest('[data-ds-modal]');
      if (modal) closeModal(modal.getAttribute('data-ds-modal-id'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openModals.size > 0) {
      const ids = Array.from(openModals);
      closeModal(ids[ids.length - 1]);
      return;
    }
    trapFocus(event);
  });

  document.addEventListener('ds-modal:open', (event) => {
    if (event.detail && event.detail.id) openModal(event.detail.id);
  });

  document.addEventListener('ds-modal:close', (event) => {
    if (event.detail && event.detail.id) closeModal(event.detail.id);
  });

  if (document.readyState !== 'loading') moveModalsToBody();
  else document.addEventListener('DOMContentLoaded', moveModalsToBody);

  // Re-run after the theme editor re-renders a section (new modal markup may
  // have been injected back into its original, nested location).
  document.addEventListener('shopify:section:load', moveModalsToBody);
})();
